// src/components/NotificationsArea.tsx
import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import OrdersNotificationsModal from './OrdersNotificationsModal';
import CustomerMessagesModal from './CustomerMessagesModal';
import OrderDetailsModal from './OrderDetailsModal';
import { useNotifications } from '../hooks/useNotifications';
import { useQuestions } from '../hooks/useQuestions';
import type { Notification as OrderNotif } from '../types/Notification';


export type NotificationsAreaHandle = {
  openOrders: () => void;
  openMessages: () => void;
  openOrderDetails: (id: string) => void;
};

type Props = {
  ordersPageSize?: number;    // по умолчанию 10
  questionsPageSize?: number; // по умолчанию 5
};

const NotificationsArea = forwardRef<NotificationsAreaHandle, Props>(function NotificationsArea(
  { ordersPageSize = 10, questionsPageSize = 5 },
  ref
) {
  // заказы/уведомления
  const {
    items: allOrders,
    total,
    page,
    setPage,
    markRead,
    remove,
  } = useNotifications(ordersPageSize);

  // Модалка уведомлений показывает только новые заказы (статус 'new')
  const orders = useMemo(
    () => allOrders.filter((o: any) => o.status === 'new'),
    [allOrders]
  );

  // сообщения
  const {
    items: qItems,
    total: qTotal,
    page: qPage,
    setPage: setQPage,
    markRead: qMarkRead,
    remove: qRemove,
  } = useQuestions(questionsPageSize);

  // локальные состояния открытия модалок
  const [isOrdersOpen, setOrdersOpen] = useState(false);
  const [isMsgsOpen, setMsgsOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const selectedOrder: OrderNotif | null = useMemo(
    () => (orders.find(o => o.id === selectedOrderId) ?? null),
    [orders, selectedOrderId]
  );

  const openOrderDetails = (id: string) => {
    setSelectedOrderId(id);
    setOrdersOpen(false);
    setDetailsOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openOrders: () => setOrdersOpen(true),
    openMessages: () => setMsgsOpen(true),
    openOrderDetails: (id: string) => openOrderDetails(id),
  }), []);

  return (
    <>
      {/* Уведомления (заказы) */}
      <OrdersNotificationsModal
        open={isOrdersOpen}
        onClose={() => setOrdersOpen(false)}
        orders={orders}
        onMarkRead={markRead}
        onDelete={remove}
        onOpenOrder={openOrderDetails}
        total={total}
        page={page}
        pageSize={ordersPageSize}
        onPageChange={setPage}
      />

      {/* Детали заказа */}
      <OrderDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        order={selectedOrder}
        onMarkRead={(ids) => { markRead(ids); }}
        onDelete={(ids) => { remove(ids); setDetailsOpen(false); }}
      />

      {/* Сообщения от покупателей */}
      <CustomerMessagesModal
        open={isMsgsOpen}
        onClose={() => setMsgsOpen(false)}
        questions={qItems}
        onMarkRead={qMarkRead}
        onDelete={qRemove}
        total={qTotal}
        page={qPage}
        pageSize={questionsPageSize}
        onPageChange={setQPage}
      />
    </>
  );
});

export default NotificationsArea;
